import React, { useEffect, useRef, useState } from "react";
import IPhoneMockup from "@/components/IPhoneMockup";
import { useLanguage } from "@/context/LanguageContext";
import { APP_SCREENS, APP_SCREENS_ALT } from "@/lib/assets";

// All steps now use real iPhone screenshots (9:19.5 aspect).
// The crossfade between screen changes is handled inside IPhoneMockup
// via framer-motion AnimatePresence on the <img> element.
const screenImageMap = {
  map: { src: APP_SCREENS.map, alt: APP_SCREENS_ALT.map },
  restaurant: { src: APP_SCREENS.restaurant, alt: APP_SCREENS_ALT.restaurant },
  quality: { src: APP_SCREENS.quality, alt: APP_SCREENS_ALT.quality },
  search: { src: APP_SCREENS.search, alt: APP_SCREENS_ALT.search },
  saved: { src: APP_SCREENS.saved, alt: APP_SCREENS_ALT.saved },
};

export default function StickyPhoneStory() {
  const { t } = useLanguage();
  const steps = t("home.howItWorks.steps");
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Sticky observer is desktop-only — on mobile each step renders its own phone inline.
    if (window.matchMedia("(max-width: 1023px)").matches) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let best = -1;
        let bestRatio = 0;
        entries.forEach((entry) => {
          if (entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            best = Number(entry.target.dataset.idx);
          }
        });
        if (best >= 0 && bestRatio > 0.2) setActive(best);
      },
      { rootMargin: "-30% 0px -30% 0px", threshold: [0.2, 0.5, 0.8] }
    );
    refs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [steps.length]);

  const activeKey = steps[active]?.screen || "map";
  const activeImg = screenImageMap[activeKey] || screenImageMap.map;

  // Preload all step images so crossfades are instant.
  useEffect(() => {
    Object.values(screenImageMap).forEach((s) => {
      if (!s.src) return;
      const img = new Image();
      img.src = s.src;
    });
  }, []);

  return (
    <div data-testid="sticky-phone-story">
      {/* ========== DESKTOP ≥1024px — sticky phone + scrolling steps ========== */}
      <div className="hidden lg:grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="sticky-phone">
            <div className="relative mx-auto" style={{ width: 280, height: 280 * (19.5 / 9) }}>
              <IPhoneMockup
                width={280}
                image={activeImg.src}
                alt={activeImg.alt}
                imgKey={activeKey}
                testId={`sticky-phone-${activeKey}`}
                priority
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col gap-24">
          {steps.map((s, i) => (
            <div
              key={i}
              ref={(el) => (refs.current[i] = el)}
              data-idx={i}
              data-testid={`story-step-${i}`}
              className={`transition-opacity duration-300 ${active === i ? "opacity-100" : "opacity-70"}`}
            >
              <div className="font-mono text-[12px] text-accent mb-2">STEP 0{i + 1}</div>
              <div className="display-sm mb-3">{s.title}</div>
              <div className="body-md max-w-md">{s.body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ========== MOBILE <1024px — inline step + own phone, no sticky ========== */}
      <div className="lg:hidden flex flex-col gap-20">
        {steps.map((s, i) => {
          const img = screenImageMap[s.screen] || screenImageMap.map;
          return (
            <div key={i} data-testid={`story-step-mobile-${i}`} className="mobile-story-step">
              <div className="step-copy">
                <div className="font-mono text-[12px] text-accent mb-2">STEP 0{i + 1}</div>
                <div className="display-sm mb-3 text-primary">{s.title}</div>
                <div className="body-md">{s.body}</div>
              </div>
              <div className="flex justify-center mt-8">
                <IPhoneMockup
                  width={260}
                  image={img.src}
                  alt={img.alt}
                  testId={`story-phone-mobile-${i}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
